import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portfolio = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(`Error loading portfolio: ${err.message || 'Unable to connect to server'}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading case studies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={fetchClients} className="bg-[#F4B400] text-black hover:bg-[#d49f00]">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white pt-24">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our <span className="text-[#F4B400]">Portfolio</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Explore our client case studies and success stories
            </p>
          </div>
        </div>
      </section>

      {/* Client Cards Grid */}
      <section className="py-16 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {clients.map((client) => (
              <Card 
                key={client._id}
                className="group cursor-pointer overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#F4B400]"
                onClick={() => navigate(`/portfolio/${client.slug}`)}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={client.coverImage} 
                    alt={client.clientName}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-[#F4B400] text-black hover:bg-[#d49f00]">
                      {client.year}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3 group-hover:text-[#F4B400] transition-colors">
                    {client.clientName}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-2">
                    {client.description}
                  </p>
                  
                  {/* Service Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {client.servicesPerformed.slice(0, 3).map((service, index) => (
                      <Badge 
                        key={index} 
                        variant="outline"
                        className="text-xs border-gray-300 text-gray-700"
                      >
                        {service}
                      </Badge>
                    ))}
                    {client.servicesPerformed.length > 3 && (
                      <Badge 
                        variant="outline"
                        className="text-xs border-[#F4B400] text-[#F4B400]"
                      >
                        +{client.servicesPerformed.length - 3} more
                      </Badge>
                    )}
                  </div>

                  <Button
                    className="w-full bg-black text-[#F4B400] border border-[#F4B400] hover:bg-[#F4B400] hover:text-black transition-all duration-300"
                  >
                    View Case Study
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {clients.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-600 text-lg">No clients found</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
